import { ButtonLink } from "@/components/ui/button-link";
import { Heart, Loader2, Phone } from "lucide-react";
import { ButtonProps } from "react-aria-components";
import { Button } from "../rac/button";

export interface ShortlistButtonProps extends ButtonProps {
	handleShortList?: () => void;
	isLoading?: boolean;
	isShortlisted?: boolean;
}
export function ShortlistButton({
	handleShortList,
	isLoading,
	isShortlisted,
	isDisabled,
	className,
	...props
}: ShortlistButtonProps) {
	const title = !isDisabled ? { title: isShortlisted ? "Shortlisted" : "Shortlist" } : {};

	// Extract onPress from props to wrap it
	const { onPress, ...restProps } = props;

	const handleClick = (event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		handleShortList?.();
	};

	const handlePress = (e: Parameters<NonNullable<ButtonProps["onPress"]>>[0]) => {
		// Call onPress callback if provided
		onPress?.(e);
	};

	const ariaLabel = isShortlisted ? "Remove from shortlist" : "Add to shortlist";

	return (
		<Button
			variant={isShortlisted ? "bareshell" : "outline"}
			size="icon"
			className={`w-10 h-10 rounded-full border-gray-300 hover:bg-gray-50 transition-colors data-[hovered]:translate-y-0 ${className || ""}`}
			isPending={isLoading}
			isDisabled={isLoading || isDisabled}
			{...(handleShortList && { onClick: handleClick })}
			{...(onPress && { onPress: handlePress })}
			aria-label={ariaLabel}
			{...restProps}
		>
			<span {...title}>
				{isLoading ? (
					<Loader2 className="w-5 h-5 animate-spin" />
				) : isShortlisted ? (
					<Heart className="w-5 h-5 fill-primary text-primary-700" />
				) : (
					<Heart className="w-5 h-5" />
				)}
			</span>
		</Button>
	);
}
export interface RequestCallbackButtonProps extends ButtonProps {
	handleRequestCallback?: () => void;
	isLoading?: boolean;
}
export function RequestCallbackButton({ handleRequestCallback, isLoading, ...props }: RequestCallbackButtonProps) {
	const handleClick = (event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		handleRequestCallback?.();
	};

	return (
		<Button
			variant="outline"
			size="icon"
			className="w-10 h-10 rounded-full border-gray-300 hover:bg-gray-50 transition-colors data-[hovered]:translate-y-0"
			isPending={isLoading}
			isDisabled={isLoading}
			{...(handleRequestCallback && { onClick: handleClick })}
			aria-label="Request callback"
			{...props}
		>
			{isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Phone className="w-5 h-5" />}
		</Button>
	);
}
export interface RequestVisitButtonProps extends ButtonProps {
	handleRequestVisit?: () => void;
	requestVisitTitle?: string;
	innerRef?: React.RefObject<HTMLButtonElement | null>;
	isLoading?: boolean;
}
export function RequestVisitButton({
	handleRequestVisit,
	requestVisitTitle = "Request Visit",
	innerRef,
	isLoading,
	...props
}: RequestVisitButtonProps) {
	const handleClick = (event: React.MouseEvent) => {
		event.stopPropagation();
		handleRequestVisit?.();
	};

	return (
		<Button
			className="flex-1 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-colors data-[hovered]:translate-y-0"
			isPending={isLoading}
			isDisabled={isLoading}
			{...(handleRequestVisit && { onClick: handleClick })}
			ref={innerRef}
			{...props}
		>
			{isLoading ? (
				<div className="flex items-center gap-2">
					<Loader2 className="w-4 h-4 animate-spin" />
					Loading...
				</div>
			) : (
				requestVisitTitle
			)}
		</Button>
	);
}
export interface ViewDetailsButtonProps extends ButtonProps {
	href: string;
	onClick?: (event: React.MouseEvent) => void;
}
export function ViewDetailsButton({ onClick, href }: ViewDetailsButtonProps) {
	return (
		<ButtonLink
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			variant="outline"
			className="flex-1 rounded-full border-gray-300 hover:bg-gray-50 transition-colors"
			onClick={onClick}
			aria-label="Open in tab"
		>
			Open in tab
		</ButtonLink>
	);
}

export interface MakeOfferButtonProps extends ButtonProps {
	handleMakeOffer?: () => void;
	isLoading?: boolean;
}
export function MakeOfferButton({ handleMakeOffer, isLoading, ...props }: MakeOfferButtonProps) {
	const handleClick = (event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		handleMakeOffer?.();
	};

	return (
		<Button
			variant="outline"
			className="flex-1 rounded-full border-gray-300 hover:bg-gray-50 transition-colors data-[hovered]:translate-y-0"
			isPending={isLoading}
			isDisabled={isLoading}
			{...(handleMakeOffer && { onClick: handleClick })}
			aria-label="Make an offer"
			{...props}
		>
			{isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Make Offer"}
		</Button>
	);
}
